<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useModals } from "@/composables/useModals";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import BaseButtonUploadReceipt from "@/components/ui/BaseButtonUploadReceipt.vue";

const { openModal, modalInfo } = useModals();
const storeRequests = useRequestsStore();
const storeSettings = useSettingsStore();

const isMobile = computed(() => storeSettings.widthWindow !== null && storeSettings.widthWindow <= 576);
const isShowList = ref(true);

const ShowList = () => {
	if (isMobile.value) {
		isShowList.value = !isShowList.value;
	}
};

watch(isMobile, (mobile) => {
	isShowList.value = !mobile;
}, { immediate: true });

const sourcesList = {
	welcome: "Приветственные листочки",
	receipt: "За товары в чеке",
	game_match3: "За игру «3 в ряд»",
	game_leaffall: "За игру «Листопад»",
	prize: "Списание за накопительный приз",
	weekly_draw: "Билет еженедельного розыгрыша",
	grand_draw: "Билет главного розыгрыша",
	admin: "Ручная операция администратора",
	receipt_correction: "Корректировка по чеку",
	referral: "Реферальный бонус",
	promo_code: "Начисление за промокод",
	yandex_outage_bonus: "Бонус в связи со сбоем сервисов Яндекс",
};

const source = (status) => {
	return sourcesList[status];
};

const operator = (type) => {
	return type === "credit" ? "+" : "";
};


onMounted(async () => {
});
</script>
<template>
	<div class="user-balance">
		<div class="user-balance__inner">
			<div class="user-balance__info">
				<h2 class="user-balance__title profile-title">Баланс листочков</h2>
				<div class="user-balance__value">
					{{ storeRequests.userData?.points_balance || 0 }}
					<svg width="32" height="32" class="leaf"><use href="#leaf"></use></svg>
				</div>
				<button @click="openModal('modalInfo', { content: modalInfo[0], size: 'w400' })" class="user-balance__btn-text btn-text">Как накопить листочки?</button>
				<BaseButtonUploadReceipt type-btn="upload_check_lk" v-if="!storeSettings.isTelegramPage" class="user-balance__btn --small">Загрузить чек</BaseButtonUploadReceipt>

			</div>
			<div class="user-balance__points" v-if="storeRequests.pointsList.length">
				<button :class="['user-balance__select-btn', { active: !isShowList }]" @click="ShowList">история начислений</button>
				<Transition>
					<ul :class="['user-balance__list list-scrollbar', { active: !isShowList }]" v-show="isShowList">
						<li class="user-balance__item" v-for="point of storeRequests.pointsList" :key="point.id">
							<span>{{ source(point.source) }}</span>
							<span>{{ operator(point.operation_type) }}{{ point.amount }}</span>
						</li>
					</ul>
				</Transition>
			</div>
		</div>
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
.user-balance {
	@extend .base-bg-light;
	border: 1px solid var(--border-accent-2-3);
	border-radius: 4rem;
	padding: 5.2rem 4rem 3.5rem 4rem;
	backdrop-filter: blur(15.399999618530273px);
	box-shadow: inset 0 13px 35px 0 rgba(255, 255, 255, 0.14), 0 16px 33px 0 rgba(0, 0, 0, 0.25);
	background: linear-gradient(143deg, #165230 0%, #0b2e1b 100%);
	@media (max-width: vars.$x480) {
		border-radius: 2.4rem;
		padding: 3rem 2.4rem 3rem 2.4rem;
	}
	&__inner {
		display: grid;
		grid-template-columns: 37rem 1fr;
		gap: 2.8rem;
		align-items: start;
		@media (max-width: vars.$xx) {
			grid-template-columns: 1fr;
			gap: 3.3rem;
		}
	}

	&__info {
		display: grid;
		gap: 1.7rem;
		@media (max-width: vars.$x480) {
			gap: 1rem;
		}
	}

	&__title {
	}

	&__value {
		display: flex;
		align-items: center;
		gap: 0.5rem;

		font-family: var(--second-family);
		font-weight: 700;
		font-size: 4.4rem;
		line-height: 110%;
		text-transform: uppercase;
		color: var(--text-accent-2-100);
		svg {
			width: 3.2rem;
			height: 3.2rem;
		}
	}

	&__btn-text {
		text-align: start;
	}

	&__btn {
		min-height: 4.8rem;
		width: fit-content;
		@media (max-width: vars.$x480) {
			width: 100%;
		}
	}

	&__points {
	}

	&__select-btn {
		width: 100%;
		display: none;
		align-items: center;
		justify-content: space-between;

		font-family: var(--second-family);
		font-weight: 700;
		font-size: 1.4rem;
		line-height: 110%;
		text-transform: uppercase;
		color: var(--text-accent-2-100);
		margin-bottom: 3rem;
		@media (max-width: vars.$xx) {
			display: flex;
		}
		@media (max-width: vars.$x480) {
			margin-bottom: 2rem;
		}
		&.active {
			&::after {
				transform: rotateX(0deg);
			}
		}
		&::after {
			content: "";
			width: 0px;
			height: 0px;
			border-style: solid;
			border-width: 0.9rem 0.85rem 0 0.85rem;
			border-color: #ffd08e transparent transparent transparent;
			transform: rotateX(180deg);
			transition: transform var(--transition) ease-in;
		}
	}

	&__list {
		width: 100%;
		max-height: 21.5rem;
		padding-right: 2rem;

		@media (max-width: vars.$x480) {
			max-height: 27rem;
			padding-right: 15px;
		}
	}

	&__item {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding: 0.7rem 0;
		border-bottom: 1px solid var(--border-accent-2-4);
		@media (max-width: vars.$x480) {
			align-items: start;
		}
		&:first-child {
			padding-top: 0;
		}
		span {
			font-weight: 500;
			font-size: 1.4rem;
			line-height: 140%;
			color: var(--text-inverse-100);
			&:last-child {
				font-family: var(--third-family);
				font-weight: 500;
				font-size: 1.6rem;
				line-height: 140%;
				color: var(--text-accent-2-100);
			}
		}
	}
}

.v-enter-active,
.v-leave-active {
	transition: 0.5s ease;
}

.v-enter-from,
.v-leave-to {
	opacity: 0;
	transform: translateY(10px);
}
</style>
